// src/components/pages/surveys/SurveyResultsScreen.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Divider, 
  Button, 
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Chip,
  Stack,
  Avatar,
  Tooltip,
  IconButton,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  Tab,
  Tabs,
  TabPanel
} from '@mui/material';
import { 
  Poll as PollIcon, 
  ArrowBack as ArrowBackIcon,
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  TextFields as TextFieldsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  QuestionAnswer as QuestionAnswerIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  ExpandMore as ExpandMoreIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  CheckBox as CheckBoxIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase';

// Utilizamos Chart.js que ya está en uso en el Dashboard
import { Pie, Bar, Line, Doughnut } from 'react-chartjs-2';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

// Paleta de colores verde pastel
const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  success: '#7ED321',
  warning: '#F5A623',
  error: '#E74C3C',
  background: '#F8FDF8',
  surface: '#FFFFFF',
  text: {
    primary: '#2C3E50',
    secondary: '#7F8C8D'
  },
  chart: {
    colors: [
      '#A8E6CF', // Verde pastel principal
      '#88D8A3', // Verde pastel secundario
      '#68C683', // Verde pastel acento
      '#7ED321', // Verde éxito
      '#F5A623', // Naranja advertencia
      '#E74C3C', // Rojo error
      '#3498DB', // Azul información
      '#9B59B6', // Púrpura
      '#1ABC9C', // Turquesa
      '#F39C12'  // Naranja
    ]
  },
  questionTypes: {
    text: '#3498DB',
    boolean: '#27AE60',
    single: '#E67E22',
    multiple: '#8E44AD'
  }
};

const SurveyResultsScreen = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [usersMap, setUsersMap] = useState({});

  // Cargar encuesta y respuestas
  useEffect(() => {
    let isMounted = true;

    const fetchSurveyData = async () => {
      try {
        setLoading(true);
        
        // Cargar datos de la encuesta
        const surveyDoc = await getDoc(doc(db, 'surveys', surveyId));
        if (!surveyDoc.exists()) {
          navigate('/admin/surveys');
          return;
        }
        
        if (isMounted) {
          setSurvey({ id: surveyDoc.id, ...surveyDoc.data() });
        }
        
        // Cargar respuestas
        const responsesQuery = query(
          collection(db, 'surveyResponses'),
          where('surveyId', '==', surveyId)
        );
        
        const responsesSnapshot = await getDocs(responsesQuery);
        const responsesData = responsesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        if (isMounted) {
          setResponses(responsesData);
        }

        // Cargar usuarios para mostrar quién ha votado
        try {
          const usersSnapshot = await getDocs(collection(db, 'users'));
          const map = {};
          usersSnapshot.docs.forEach(u => { map[u.id] = u.data(); });
          if (isMounted) setUsersMap(map);
        } catch (e) {
          console.warn('No se pudieron cargar los usuarios:', e);
        }
        
      } catch (error) {
        console.error("Error al cargar datos:", error);
        if (isMounted) {
          navigate('/admin/surveys');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSurveyData();

    return () => {
      isMounted = false;
    };
  }, [surveyId, navigate]);

  // Estadísticas generales memoizadas
  const generalStats = useMemo(() => {
    if (!survey || !responses.length) return null;

    const totalQuestions = survey.preguntas?.length || 0;
    const totalResponses = responses.length;
    const completionRate = responses.length > 0 ? 
      responses.filter(r => r.respuestas?.length === totalQuestions).length / totalResponses * 100 : 0;
    
    const responsesByDate = responses.reduce((acc, response) => {
      const date = response.createdAt ? 
        new Date(response.createdAt.seconds * 1000).toLocaleDateString() : 
        'Sin fecha';
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const avgCompletionTime = responses.reduce((acc, response) => {
      if (response.completionTime) {
        return acc + response.completionTime;
      }
      return acc;
    }, 0) / responses.length || 0;

    return {
      totalResponses,
      totalQuestions,
      completionRate: Math.round(completionRate),
      responsesByDate,
      avgCompletionTime: Math.round(avgCompletionTime / 60) // en minutos
    };
  }, [survey, responses]);

  // Nombre del votante de una respuesta (soporta distintos nombres de campo)
  const getVoter = useCallback((response) => {
    const uid = response.userId || response.uid || response.usuarioId || response.user?.id;
    const u = (uid && usersMap[uid]) || {};
    const fullName = `${u.firstName || ''} ${u.lastName || ''}`.trim();
    const name = fullName
      || response.userName || response.nombre || response.displayName
      || u.displayName || u.email || response.userEmail || response.email
      || 'Usuario desconocido';
    return { uid, name, email: u.email || response.userEmail || response.email || '' };
  }, [usersMap]);

  // Texto legible de una respuesta
  const answerLabel = useCallback((question, value) => {
    if (value === null || value === undefined || value === '') return '—';
    const opt = (i) => question.opciones?.[i] ?? (typeof i === 'number' ? `Opción ${i + 1}` : String(i));
    if (question.tipo === 'boolean') return value ? 'Sí' : 'No';
    if (question.tipo === 'single') return opt(value);
    if (question.tipo === 'multiple') return Array.isArray(value) ? value.map(opt).join(', ') : String(value);
    return String(value);
  }, []);

  // Procesar datos para gráficos y estadísticas
  const processQuestionStats = useCallback((question) => {
    if (!responses.length) return null;
    
    const stats = {
      totalResponses: responses.length,
      answered: 0,
      skipped: 0,
      results: {},
      textResponses: [],
      voters: {}
    };
    const addVoter = (label, response) => {
      (stats.voters[label] = stats.voters[label] || []).push(getVoter(response).name);
    };
    
    // Contar respuestas
    responses.forEach(response => {
      const answer = response.respuestas?.find(r => r.questionId === question.id);
      
      if (answer && answer.respuesta !== null && answer.respuesta !== undefined && answer.respuesta !== '') {
        stats.answered++;
        
        if (question.tipo === 'text') {
          stats.textResponses.push({
            respuesta: answer.respuesta,
            timestamp: response.createdAt,
            voter: getVoter(response).name
          });
        } else if (question.tipo === 'boolean') {
          const value = answer.respuesta ? 'Sí' : 'No';
          stats.results[value] = (stats.results[value] || 0) + 1;
          addVoter(value, response);
        } else if (question.tipo === 'single') {
          const optionIndex = answer.respuesta;
          const optionText = question.opciones?.[optionIndex] || `Opción ${optionIndex + 1}`;
          stats.results[optionText] = (stats.results[optionText] || 0) + 1;
          addVoter(optionText, response);
        } else if (question.tipo === 'multiple') {
          if (Array.isArray(answer.respuesta)) {
            answer.respuesta.forEach(optIndex => {
              const optionText = question.opciones?.[optIndex] || `Opción ${optIndex + 1}`;
              stats.results[optionText] = (stats.results[optionText] || 0) + 1;
              addVoter(optionText, response);
            });
          }
        }
      } else {
        stats.skipped++;
      }
    });
    
    return stats;
  }, [responses, getVoter]);

  // Generar datos para gráfico de pastel
  const getPieChartData = useCallback((stats) => {
    if (!stats || !stats.results || Object.keys(stats.results).length === 0) return null;
    
    const labels = Object.keys(stats.results);
    const data = Object.values(stats.results);
    
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: COLORS.chart.colors.slice(0, labels.length),
          borderColor: COLORS.surface,
          borderWidth: 2,
          hoverBorderWidth: 3,
          hoverBorderColor: COLORS.accent
        },
      ],
    };
  }, []);

  // Generar datos para gráfico de barras
  const getBarChartData = useCallback((stats) => {
    if (!stats || !stats.results || Object.keys(stats.results).length === 0) return null;
    
    const labels = Object.keys(stats.results);
    const data = Object.values(stats.results);
    
    return {
      labels,
      datasets: [
        {
          label: 'Respuestas',
          data,
          backgroundColor: COLORS.accent + '80',
          borderColor: COLORS.accent,
          borderWidth: 2,
          borderRadius: 8,
          borderSkipped: false,
        },
      ],
    };
  }, []);

  // Generar datos para gráfico de línea temporal
  const getTimelineData = useCallback(() => {
    if (!generalStats?.responsesByDate) return null;

    const sortedDates = Object.keys(generalStats.responsesByDate).sort();
    const data = sortedDates.map(date => generalStats.responsesByDate[date]);

    return {
      labels: sortedDates,
      datasets: [
        {
          label: 'Respuestas por día',
          data,
          borderColor: COLORS.accent,
          backgroundColor: COLORS.accent + '20',
          tension: 0.4,
          fill: true,
          pointBackgroundColor: COLORS.accent,
          pointBorderColor: COLORS.surface,
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    };
  }, [generalStats]);

  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return 'Fecha desconocida';
    try {
      return new Date(timestamp.seconds * 1000).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  }, []);

  const getQuestionTypeIcon = useCallback((type) => {
    const iconProps = { fontSize: 'small', sx: { color: COLORS.questionTypes[type] || COLORS.text.secondary } };
    
    switch (type) {
      case 'text':
        return <TextFieldsIcon {...iconProps} />;
      case 'boolean':
        return <CheckCircleIcon {...iconProps} />;
      case 'single':
        return <RadioButtonCheckedIcon {...iconProps} />;
      case 'multiple':
        return <CheckBoxIcon {...iconProps} />;
      default:
        return <QuestionAnswerIcon {...iconProps} />;
    }
  }, []);

  const getQuestionTypeName = useCallback((type) => {
    const types = {
      text: 'Texto libre',
      boolean: 'Sí/No',
      single: 'Selección única',
      multiple: 'Selección múltiple'
    };
    return types[type] || 'Desconocido';
  }, []);

  // Componente de estadística
  const StatsCard = ({ title, value, subtitle, color, icon, trend }) => (
    <Card sx={{ 
      height: '100%',
      background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
      border: `1px solid ${color}30`,
      transition: 'transform 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 25px ${color}20`
      }
    }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            backgroundColor: `${color}20`,
            color: color 
          }}>
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
              {value}
            </Typography>
            <Typography variant="body1" sx={{ color: COLORS.text.primary, fontWeight: 500 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {trend && (
            <Box sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ color: COLORS.success }} />
              <Typography variant="caption" sx={{ color: COLORS.success }}>
                {trend}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const ResultsSkeleton = () => (
    <Box>
      <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
      <Skeleton variant="text" width="100%" height={20} sx={{ mb: 3 }} />
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[...Array(4)].map((_, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card>
              <CardContent>
                <Skeleton variant="circular" width={40} height={40} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="text" width="60%" height={20} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {[...Array(3)].map((_, index) => (
        <Card key={index} sx={{ mb: 3 }}>
          <CardContent>
            <Skeleton variant="text" width="70%" height={32} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={300} />
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  // Exportar respuestas a CSV (una fila por votante, una columna por pregunta)
  const handleExport = () => {
    if (!survey || !responses.length) return;
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Votante', 'Email', 'Fecha', ...(survey.preguntas || []).map(q => q.texto)];
    const rows = responses.map(response => {
      const voter = getVoter(response);
      const date = response.createdAt?.seconds
        ? new Date(response.createdAt.seconds * 1000).toLocaleString('es-ES')
        : '';
      const answers = (survey.preguntas || []).map(q => {
        const a = response.respuestas?.find(r => r.questionId === q.id);
        return answerLabel(q, a?.respuesta);
      });
      return [voter.name, voter.email, date, ...answers];
    });
    const csv = [header, ...rows].map(r => r.map(esc).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `encuesta-${(survey.título || survey.id).replace(/[^\w-]+/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Box sx={{ backgroundColor: COLORS.background, minHeight: '100vh', p: 3 }}>
        <Container maxWidth="xl">
          <Box sx={{ mb: 3 }}>
            <Skeleton variant="rectangular" width={150} height={36} sx={{ borderRadius: 2 }} />
          </Box>
          <ResultsSkeleton />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: COLORS.background, minHeight: '100vh', p: 3 }}>
      <Container maxWidth="xl">
        {/* Header Navigation */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/surveys')}
            sx={{
              color: COLORS.accent,
              '&:hover': { backgroundColor: COLORS.accent + '10' },
              mb: 2
            }}
          >
            Volver a encuestas
          </Button>
        </Box>

        {/* Survey Header */}
        <Paper sx={{ 
          p: 4, 
          mb: 4,
          backgroundColor: COLORS.surface,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          background: `linear-gradient(135deg, ${COLORS.primary}10 0%, ${COLORS.surface} 100%)`
        }}>
          <Stack direction="row" alignItems="flex-start" spacing={3} sx={{ mb: 3 }}>
            <Avatar sx={{ 
              bgcolor: COLORS.accent + '20', 
              width: 64, 
              height: 64,
              border: `2px solid ${COLORS.accent}30`
            }}>
              <AnalyticsIcon sx={{ fontSize: 32, color: COLORS.accent }} />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 1 }}>
                {survey.título}
              </Typography>
              <Typography variant="body1" sx={{ color: COLORS.text.secondary, mb: 2 }}>
                {survey.descripción}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Chip
                  label={survey.active ? 'Activa' : 'Inactiva'}
                  color={survey.active ? 'success' : 'default'}
                  sx={{
                    backgroundColor: survey.active ? COLORS.success + '20' : COLORS.text.secondary + '20',
                    color: survey.active ? COLORS.success : COLORS.text.secondary,
                    fontWeight: 'bold'
                  }}
                />
                <Chip
                  icon={<ScheduleIcon fontSize="small" />}
                  label={`Creada: ${formatDate(survey.createdAt)}`}
                  variant="outlined"
                  sx={{ borderColor: COLORS.accent + '50' }}
                />
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Compartir resultados">
                <IconButton sx={{ color: COLORS.secondary }}>
                  <ShareIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Exportar datos">
                <IconButton onClick={handleExport} disabled={!responses.length} sx={{ color: COLORS.accent }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>

        {/* Statistics Cards */}
        {generalStats && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatsCard
                title="Respuestas Totales"
                value={generalStats.totalResponses}
                color={COLORS.accent}
                icon={<AssignmentIcon />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatsCard
                title="Tasa Completitud"
                value={`${generalStats.completionRate}%`}
                subtitle="respuestas completas"
                color={COLORS.success}
                icon={<CheckCircleIcon />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatsCard
                title="Preguntas"
                value={generalStats.totalQuestions}
                subtitle="en la encuesta"
                color={COLORS.secondary}
                icon={<QuestionAnswerIcon />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatsCard
                title="Tiempo Promedio"
                value={`${generalStats.avgCompletionTime}min`}
                subtitle="para completar"
                color={COLORS.warning}
                icon={<ScheduleIcon />}
              />
            </Grid>
          </Grid>
        )}

        {/* Timeline Chart */}
        {generalStats && getTimelineData() && (
          <Paper sx={{ 
            p: 3, 
            mb: 4,
            backgroundColor: COLORS.surface,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 3 }}>
              📈 Evolución de Respuestas
            </Typography>
            <Box sx={{ height: 300, position: 'relative', minWidth: 0 }}>
              <Line 
                data={getTimelineData()} 
                options={{ 
                  maintainAspectRatio: false,
                  responsive: true,
                  plugins: {
                    legend: {
                      display: false
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        precision: 0
                      }
                    }
                  }
                }} 
              />
            </Box>
          </Paper>
        )}

        {/* Tabs for different views */}
        <Paper sx={{ 
          backgroundColor: COLORS.surface,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          mb: 4
        }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              borderBottom: `1px solid ${COLORS.accent}20`,
              '& .MuiTab-root': {
                color: COLORS.text.secondary,
                '&.Mui-selected': {
                  color: COLORS.accent,
                  fontWeight: 'bold'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: COLORS.accent
              }
            }}
          >
            <Tab label="📊 Resultados Detallados" />
            <Tab label="📋 Resumen Ejecutivo" />
            <Tab label="📝 Respuestas de Texto" />
            <Tab label="👥 Votantes" />
          </Tabs>
        </Paper>

        {responses.length === 0 ? (
          <Paper sx={{ 
            p: 6, 
            textAlign: 'center',
            backgroundColor: COLORS.surface,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <PollIcon sx={{ fontSize: 80, color: COLORS.text.secondary, mb: 3 }} />
            <Typography variant="h5" sx={{ color: COLORS.text.primary, fontWeight: 'bold', mb: 2 }}>
              Aún no hay respuestas
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              Cuando los usuarios completen la encuesta, podrás ver los resultados detallados aquí.
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<VisibilityIcon />}
              onClick={() => navigate(`/admin/surveys/preview/${surveyId}`)}
              sx={{
                backgroundColor: COLORS.accent,
                '&:hover': { backgroundColor: COLORS.secondary }
              }}
            >
              Vista previa de la encuesta
            </Button>
          </Paper>
        ) : (
          <Box>
            {/* Tab Content */}
            {activeTab === 0 && (
              <Grid container spacing={3}>
                {survey.preguntas?.map((question, index) => {
                  const stats = processQuestionStats(question);
                  if (!stats) return null;
                  
                  const answerPercentage = Math.round((stats.answered / stats.totalResponses) * 100);
                  
                  return (
                    <Grid size={{ xs: 12 }} key={question.id}>
                      <Paper sx={{ 
                        p: 4,
                        backgroundColor: COLORS.surface,
                        borderRadius: 3,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        border: `1px solid ${COLORS.questionTypes[question.tipo] || COLORS.accent}20`
                      }}>
                        {/* Question Header */}
                        <Box sx={{ mb: 3 }}>
                          <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
                            <Chip
                              icon={getQuestionTypeIcon(question.tipo)}
                              label={getQuestionTypeName(question.tipo)}
                              size="small"
                              sx={{
                                backgroundColor: `${COLORS.questionTypes[question.tipo] || COLORS.accent}20`,
                                color: COLORS.questionTypes[question.tipo] || COLORS.accent,
                                fontWeight: 'bold'
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Pregunta {index + 1} de {survey.preguntas.length}
                            </Typography>
                          </Stack>
                          
                          <Typography variant="h6" sx={{ 
                            fontWeight: 'bold', 
                            color: COLORS.text.primary,
                            mb: 2 
                          }}>
                            {question.texto}
                          </Typography>

                          {question.requerida && (
                            <Chip
                              label="Obligatoria"
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          )}
                        </Box>
                        
                        {/* Response Statistics */}
                        <Box sx={{ mb: 4 }}>
                          <Grid container spacing={3} alignItems="center">
                            <Grid size={{ xs: 12, md: 8 }}>
                              <Box sx={{ mb: 2 }}>
                                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Tasa de respuesta
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                                    {answerPercentage}%
                                  </Typography>
                                </Stack>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={answerPercentage} 
                                  sx={{ 
                                    height: 8, 
                                    borderRadius: 4,
                                    backgroundColor: COLORS.background,
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: answerPercentage > 70 ? COLORS.success : 
                                                      answerPercentage > 40 ? COLORS.warning : COLORS.error,
                                      borderRadius: 4,
                                    },
                                  }}
                                />
                              </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Stack spacing={1}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <CheckCircleIcon sx={{ fontSize: 16, color: COLORS.success }} />
                                    <Typography variant="body2" color="text.secondary">
                                      Respondidas:
                                    </Typography>
                                  </Stack>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {stats.answered}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <CancelIcon sx={{ fontSize: 16, color: COLORS.text.secondary }} />
                                    <Typography variant="body2" color="text.secondary">
                                      Omitidas:
                                    </Typography>
                                  </Stack>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {stats.skipped}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Grid>
                          </Grid>
                        </Box>
                        
                        <Divider sx={{ my: 3 }} />
                        
                        {/* Question Results */}
                        {question.tipo === 'text' ? (
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 2 }}>
                              💬 Respuestas de texto
                            </Typography>
                            {stats.textResponses && stats.textResponses.length > 0 ? (
                              <Box sx={{ mt: 2, maxHeight: 400, overflow: 'auto' }}>
                                {stats.textResponses.map((response, i) => (
                                  <Card key={i} variant="outlined" sx={{ 
                                    mb: 2, 
                                    borderColor: COLORS.accent + '30',
                                    '&:hover': { borderColor: COLORS.accent }
                                  }}>
                                    <CardContent sx={{ py: 2 }}>
                                      <Typography variant="body1" sx={{ mb: 1 }}>
                                        "{response.respuesta}"
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {response.voter} · {response.timestamp ? formatDate(response.timestamp) : 'Sin fecha'}
                                      </Typography>
                                    </CardContent>
                                  </Card>
                                ))}
                              </Box>
                            ) : (
                              <Alert severity="info" sx={{ borderRadius: 2 }}>
                                No hay respuestas de texto para mostrar
                              </Alert>
                            )}
                          </Box>
                        ) : (
                          <Box>
  {stats && Object.keys(stats.results).length > 0 ? (
    <Grid container spacing={3}>
      {/* Charts */}
      <Grid size={{ xs: 12, lg: 6 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 2 }}>
            🥧 Distribución de respuestas
          </Typography>
          <Box sx={{ height: 350, position: 'relative', minWidth: 0 }}>
            <Doughnut 
              data={getPieChartData(stats)} 
              options={{ 
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      padding: 20,
                      usePointStyle: true
                    }
                  }
                }
              }} 
            />
          </Box>
        </Box>
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 2 }}>
            📊 Comparación de opciones
          </Typography>
          <Box sx={{ height: 350, position: 'relative', minWidth: 0 }}>
            <Bar 
              data={getBarChartData(stats)} 
              options={{ 
                maintainAspectRatio: false,
                responsive: true,
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      precision: 0
                    }
                  }
                },
                plugins: {
                  legend: {
                    display: false
                  }
                }
              }} 
            />
          </Box>
        </Box>
      </Grid>
      
      {/* Detailed Results */}
      <Grid size={{ xs: 12 }}>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 3 }}>
            📋 Detalle de respuestas
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(stats.results).map(([label, count], i) => {
              const percentage = Math.round((count / stats.answered) * 100);
              const color = COLORS.chart.colors[i % COLORS.chart.colors.length];
              
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                  <Card sx={{ 
                    p: 2,
                    border: `2px solid ${color}30`,
                    backgroundColor: `${color}08`,
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 25px ${color}20`
                    }
                  }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: color,
                        borderRadius: '50%'
                      }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                          {label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {count} respuestas ({percentage}%)
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color }}>
                        {count}
                      </Typography>
                    </Stack>
                    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(stats.voters[label] || []).map((name, vi) => (
                        <Chip key={vi} size="small" label={name} variant="outlined" />
                      ))}
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Grid>
    </Grid>
  ) : (
    <Alert severity="info" sx={{ borderRadius: 2 }}>
      No hay datos suficientes para mostrar gráficos
    </Alert>
  )}
</Box>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* Executive Summary Tab */}
            {activeTab === 1 && (
              <Paper sx={{ 
                p: 4,
                backgroundColor: COLORS.surface,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 3 }}>
                  📊 Resumen Ejecutivo
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Card sx={{ p: 3, mb: 3, backgroundColor: COLORS.background }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 2 }}>
                        📈 Métricas Principales
                      </Typography>
                      <List>
                        <ListItem>
                          <ListItemIcon>
                            <PeopleIcon sx={{ color: COLORS.accent }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${generalStats.totalResponses} personas han respondido`}
                            secondary={`De un total estimado de participantes`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircleIcon sx={{ color: COLORS.success }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${generalStats.completionRate}% de tasa de completitud`}
                            secondary="Porcentaje de encuestas completadas totalmente"
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <ScheduleIcon sx={{ color: COLORS.warning }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${generalStats.avgCompletionTime} minutos promedio`}
                            secondary="Tiempo que toma completar la encuesta"
                          />
                        </ListItem>
                      </List>
                    </Card>

                    <Card sx={{ p: 3, backgroundColor: COLORS.background }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 2 }}>
                        🎯 Insights Clave
                      </Typography>
                      <Stack spacing={2}>
                        {survey.preguntas?.slice(0, 3).map((question, index) => {
                          const stats = processQuestionStats(question);
                          if (!stats) return null;
                          
                          const topAnswer = Object.entries(stats.results || {})
                            .sort(([,a], [,b]) => b - a)[0];
                          
                          return (
                            <Alert 
                              key={question.id} 
                              severity="info" 
                              sx={{ 
                                borderRadius: 2,
                                backgroundColor: COLORS.accent + '10',
                                border: `1px solid ${COLORS.accent}30`
                              }}
                            >
                              <Typography variant="body2">
                                <strong>Pregunta {index + 1}:</strong> {question.tipo === 'text' ? 
                                  `${stats.textResponses?.length || 0} respuestas de texto` :
                                  `La opción más elegida fue "${topAnswer?.[0] || 'N/A'}" con ${topAnswer?.[1] || 0} votos`
                                }
                              </Typography>
                            </Alert>
                          );
                        })}
                      </Stack>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Card sx={{ p: 3, backgroundColor: COLORS.primary + '10' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 2 }}>
                        🏆 Estadísticas Destacadas
                      </Typography>
                      <Stack spacing={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h3" sx={{ fontWeight: 'bold', color: COLORS.accent }}>
                            {Math.max(...Object.values(generalStats.responsesByDate || {}))}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Respuestas en el día pico
                          </Typography>
                        </Box>
                        
                        <Divider />
                        
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h3" sx={{ fontWeight: 'bold', color: COLORS.secondary }}>
                            {survey.preguntas?.filter(q => q.requerida).length || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Preguntas obligatorias
                          </Typography>
                        </Box>

                        <Divider />

                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h3" sx={{ fontWeight: 'bold', color: COLORS.success }}>
                            {Object.keys(generalStats.responsesByDate || {}).length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Días con actividad
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Text Responses Tab */}
            {activeTab === 2 && (
              <Paper sx={{ 
                p: 4,
                backgroundColor: COLORS.surface,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 3 }}>
                  📝 Todas las Respuestas de Texto
                </Typography>
                
                {survey.preguntas?.filter(q => q.tipo === 'text').map((question, index) => {
                  const stats = processQuestionStats(question);
                  if (!stats || !stats.textResponses?.length) return null;
                  
                  return (
                    <Accordion key={question.id} sx={{ mb: 2, borderRadius: 2 }}>
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: COLORS.background,
                          borderRadius: 2,
                          '&:hover': { backgroundColor: COLORS.accent + '10' }
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                          <TextFieldsIcon sx={{ color: COLORS.questionTypes.text }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {question.texto}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {stats.textResponses.length} respuestas
                            </Typography>
                          </Box>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                          {stats.textResponses.map((response, i) => (
                            <Card key={i} variant="outlined" sx={{ 
                              mb: 2, 
                              borderColor: COLORS.accent + '30',
                              '&:hover': { 
                                borderColor: COLORS.accent,
                                backgroundColor: COLORS.background
                              }
                            }}>
                              <CardContent sx={{ py: 2 }}>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                  "{response.respuesta}"
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  👤 {response.voter} · 📅 {response.timestamp ? formatDate(response.timestamp) : 'Sin fecha'}
                                </Typography>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
                
                {!survey.preguntas?.some(q => q.tipo === 'text') && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Esta encuesta no contiene preguntas de texto libre.
                  </Alert>
                )}
              </Paper>
            )}

            {/* Voters Tab */}
            {activeTab === 3 && (
              <Paper sx={{
                p: 4,
                backgroundColor: COLORS.surface,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: COLORS.text.primary, mb: 3 }}>
                  👥 Quién ha votado ({responses.length})
                </Typography>
                {responses.map(response => {
                  const voter = getVoter(response);
                  return (
                    <Accordion key={response.id} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                          <Avatar sx={{ bgcolor: COLORS.accent, width: 32, height: 32 }}>
                            {voter.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 'bold' }}>{voter.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {voter.email ? `${voter.email} · ` : ''}{formatDate(response.createdAt)}
                            </Typography>
                          </Box>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={1}>
                          {survey.preguntas?.map(question => {
                            const answer = response.respuestas?.find(r => r.questionId === question.id);
                            return (
                              <Box key={question.id}>
                                <Typography variant="body2" color="text.secondary">{question.texto}</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {answerLabel(question, answer?.respuesta)}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Paper>
            )}
          </Box>
        )}

        {/* Footer Actions */}
        <Box sx={{ mt: 6, mb: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/surveys')}
            size="large"
            sx={{
              borderColor: COLORS.accent,
              color: COLORS.accent,
              '&:hover': { borderColor: COLORS.secondary, backgroundColor: COLORS.accent + '10' }
            }}
          >
            Volver a Encuestas
          </Button>
          {responses.length > 0 && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              size="large"
              sx={{
                backgroundColor: COLORS.accent,
                '&:hover': { backgroundColor: COLORS.secondary }
              }}
            >
              Exportar Resultados
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default SurveyResultsScreen;